import { Platform } from "react-native";

import {
  deleteExpoSecureStoreItemAsync,
  getExpoSecureStoreItemAsync,
  setExpoSecureStoreItemAsync,
} from "@whoosh/utilities/src/ExpoSecureStore/expoSecureStore";

import { CLUB_SLUG_STORAGE_KEY } from "./constants";

type MaybePromise<T> = PromiseLike<T> | T;

export type ClubSlugStringStorage = {
  getItem: (key: string) => MaybePromise<null | string>;
  removeItem: (key: string) => MaybePromise<null | void>;
  setItem: (key: string, value: string) => MaybePromise<null | void>;
};

export type ParsedStoredClubSlug = {
  shouldRewrite: boolean;
  slug: string;
};

const jsonContainerLike = (value: string): boolean =>
  ['"', "[", "{"].includes(value.trimStart()[0] ?? "");

const jsonStringLike = (value: string): boolean =>
  value.trimStart().startsWith('"');

export const parseStoredClubSlug = (
  storedClubSlug: null | string
): null | ParsedStoredClubSlug => {
  if (!storedClubSlug) {
    return null;
  }

  if (!jsonStringLike(storedClubSlug)) {
    if (jsonContainerLike(storedClubSlug)) {
      return null;
    }

    return { shouldRewrite: false, slug: storedClubSlug };
  }

  try {
    const parsedClubSlug: unknown = JSON.parse(storedClubSlug);

    if (
      typeof parsedClubSlug !== "string" ||
      !parsedClubSlug ||
      jsonContainerLike(parsedClubSlug)
    ) {
      return null;
    }

    // TODO(ENG-2702): Remove JSON-string migration after all older patron clients are retired and rollback no longer
    // requires their persisted representation.
    return { shouldRewrite: true, slug: parsedClubSlug };
  } catch {
    return null;
  }
};

const getPlatformClubSlugStorage = (): ClubSlugStringStorage => {
  if (Platform.OS === "web") {
    return {
      getItem: (key) => localStorage.getItem(key),
      removeItem: (key) => localStorage.removeItem(key),
      setItem: (key, value) => localStorage.setItem(key, value),
    };
  }

  return {
    getItem: getExpoSecureStoreItemAsync,
    removeItem: deleteExpoSecureStoreItemAsync,
    setItem: setExpoSecureStoreItemAsync,
  };
};

export const getStoredClubSlug = async (
  storage: ClubSlugStringStorage = getPlatformClubSlugStorage()
): Promise<null | string> => {
  const storedClubSlug = await storage.getItem(CLUB_SLUG_STORAGE_KEY);
  const parsedClubSlug = parseStoredClubSlug(storedClubSlug);

  if (!parsedClubSlug) {
    if (storedClubSlug) {
      await storage.removeItem(CLUB_SLUG_STORAGE_KEY);
    }

    return null;
  }

  if (parsedClubSlug.shouldRewrite) {
    await storage.setItem(CLUB_SLUG_STORAGE_KEY, parsedClubSlug.slug);
  }

  return parsedClubSlug.slug;
};

export const setStoredClubSlug = async (
  clubSlug: string,
  storage: ClubSlugStringStorage = getPlatformClubSlugStorage()
): Promise<void> => {
  await storage.setItem(CLUB_SLUG_STORAGE_KEY, clubSlug);
};

export const removeStoredClubSlug = async (
  storage: ClubSlugStringStorage = getPlatformClubSlugStorage()
): Promise<void> => {
  await storage.removeItem(CLUB_SLUG_STORAGE_KEY);
};
